import React, { memo, useMemo } from "react";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  Paper,
  Chip
} from "@mui/material";
import {
  LocationOn,
  People,
  Event as EventIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";

const formatStartTime = (t) => {
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  if (isNaN(h)) return t;
  const period = h >= 12 ? "pm" : "am";
  const hour = h % 12 || 12;
  return `from ${hour}${m ? `:${String(m).padStart(2, "0")}` : ""}${period}`;
};

// --- Add safe numeric helpers (place near top of file) ---
const toNumber = (v) => {
  if (v === null || v === undefined || v === "") return NaN;
  if (typeof v === "number") return v;
  // treat hyphen or other non-numeric placeholders as NaN
  const n = Number(String(v).replace(/[,£$]/g, ""));
  return Number.isFinite(n) ? n : NaN;
};

const formatPrice = (v) => {
  // keep explicit placeholder strings (like "-") unchanged
  if (typeof v === "string" && v.trim() !== "" && isNaN(Number(v))) return v;
  const n = toNumber(v);
  return Number.isFinite(n) ? n.toFixed(2) : "-";
};

const formatNumber = (v) => {
  if (typeof v === "string" && v.trim() !== "" && isNaN(Number(v))) return v;
  const n = toNumber(v);
  return Number.isFinite(n) ? String(Math.round(n)) : "-";
};
// --- end helpers ---

// Option Selection Component
const OptionSelector = memo(({ event, onOptionSelect, localSelectedId }) => {
  // Use local selection if available, otherwise fall back to server state
  const selectedOption = localSelectedId
    ? event.options?.find(opt => opt.id === localSelectedId)
    : event.options?.find(opt => opt.isSelected);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{
          color: '#380045',
          fontWeight: 600,
          mb: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        Choose your package:
        {!selectedOption && (
          <Chip
            label="Selection required"
            size="small"
            sx={{
              backgroundColor: '#fff3e0',
              color: '#e65100',
              fontSize: '0.7rem',
              height: '20px'
            }}
          />
        )}
      </Typography>

      <RadioGroup
        value={selectedOption?.id?.toString() || ''}
      >
        <Stack spacing={1.5}>
          {event.options?.map((option) => {
            const isSelected = localSelectedId ? option.id === localSelectedId : option.isSelected;
            return (
            <Paper
              key={option.id}
              elevation={0}
              onClick={() => onOptionSelect(event.id, option.id)}
              sx={{
                p: 2,
                border: isSelected
                  ? '2px solid #380045'
                  : '1px solid rgba(0,0,0,0.1)',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: isSelected ? 'rgba(56, 0, 69, 0.03)' : 'white',
                '&:hover': {
                  borderColor: '#380045',
                  boxShadow: '0 2px 8px rgba(56, 0, 69, 0.1)'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <FormControlLabel
                  value={option.id.toString()}
                  control={
                    <Radio
                      sx={{
                        color: '#380045',
                        '&.Mui-checked': { color: '#380045' }
                      }}
                    />
                  }
                  label=""
                  sx={{ m: 0, mr: -1 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1
                  }}>
                    <Typography
                      variant="body1"
                      fontWeight={isSelected ? 700 : 600}
                      color={isSelected ? '#380045' : 'text.primary'}
                    >
                      {option.optionLabel}
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color="#380045"
                    >
                      £{formatPrice(option.price)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <People sx={{ fontSize: '1rem', color: '#666' }} />
                    <Typography variant="body2" color="text.secondary">
                      {option.numberOfDrummers} drummer{option.numberOfDrummers !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  {option.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, fontStyle: 'italic' }}
                    >
                      {option.description}
                    </Typography>
                  )}
                </Box>
                {isSelected && (
                  <CheckIcon sx={{ color: '#380045', fontSize: '1.2rem' }} />
                )}
              </Box>
            </Paper>
            );
          })}
        </Stack>
      </RadioGroup>
    </Box>
  );
});

const QuoteAccordion = memo(({ quote, quoteId, isAccepted = false, localSelections = {}, onSelectionChange, expenses = [], serverTotalPrice = null, showTotal = true }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Memoize total price calculation
  const totalPrice = useMemo(() => {
    if (!quote?.eventsList) return 0;
    return quote.eventsList.reduce((acc, ev) => {
      if (ev.hasOptions) {
        // Get selected option using local or server state
        const localSelectedId = localSelections[ev.id];
        const selectedOption = localSelectedId
          ? ev.options?.find(opt => opt.id === localSelectedId)
          : ev.options?.find(opt => opt.isSelected);
        const n = toNumber(selectedOption?.price);
        return acc + (Number.isFinite(n) ? n : 0);
      } else {
        const n = toNumber(ev?.price);
        return acc + (Number.isFinite(n) ? n : 0);
      }
    }, 0);
  }, [quote?.eventsList, localSelections]);

  // Sum of expense line totals (guards null totals)
  const expensesSum = useMemo(() => {
    if (!Array.isArray(expenses)) return 0;
    return expenses.reduce((acc, ex) => {
      const n = toNumber(ex?.total);
      return acc + (Number.isFinite(n) ? n : 0);
    }, 0);
  }, [expenses]);

  // True while the customer is still choosing packages (pre-acceptance quotes
  // with option events). The server's totalPrice can't reflect an unsaved local
  // selection, so we keep the total live off the client sum during this phase.
  const hasSelectableOptions = useMemo(
    () => !isAccepted && (quote?.eventsList || []).some((e) => e.hasOptions),
    [isAccepted, quote?.eventsList]
  );

  // Displayed grand total. Live client sum (events + expenses) while selecting
  // options; otherwise the authoritative server total, falling back to the
  // client sum for legacy quotes where totalPrice is unavailable.
  const displayTotal = hasSelectableOptions
    ? totalPrice + expensesSum
    : serverTotalPrice != null && Number.isFinite(Number(serverTotalPrice))
    ? Number(serverTotalPrice)
    : totalPrice + expensesSum;

  // Handle option selection - just update local state, no API call
  const handleOptionSelect = (eventId, optionId) => {
    if (onSelectionChange) {
      onSelectionChange(eventId, optionId);
    }
  };

  if (!quote || !quote.eventsList) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No quote data available.
        </Typography>
      </Box>
    );
  }

  // Helper to get selected option for an event (uses local selection if available)
  const getSelectedOption = (event) => {
    if (!event.hasOptions) return null;
    const localSelectedId = localSelections[event.id];
    if (localSelectedId) {
      return event.options?.find(opt => opt.id === localSelectedId);
    }
    return event.options?.find(opt => opt.isSelected);
  };

  // Helper to get display values for an event (handles both regular and option-based events)
  const getEventDisplayData = (event) => {
    if (event.hasOptions) {
      const selectedOption = getSelectedOption(event);
      if (selectedOption) {
        return {
          numberOfDrummers: selectedOption.numberOfDrummers,
          price: selectedOption.price,
          optionLabel: selectedOption.optionLabel
        };
      }
      return { numberOfDrummers: '-', price: '-', optionLabel: null };
    }
    return {
      numberOfDrummers: event.numberOfDrummers,
      price: event.price,
      optionLabel: null
    };
  };

  // Check if we should show option selector (only when not accepted and event has options)
  const shouldShowOptionSelector = (event) => {
    return !isAccepted && event.hasOptions;
  };

  const { eventsList } = quote;

  // Consistent styling
  const cardRadius = 2;
  const cardShadow = '0 1px 3px rgba(0,0,0,0.08)';

  return (
    <Box>
      {/* Line Items - Desktop View */}
      {!isMobile ? (
        <Box sx={{ mb: 4 }}>
          {eventsList.map((event, index) => (
            <Box key={event.id || index} sx={{ mb: 3 }}>
              {/* Event Header Row */}
              <TableContainer>
                <Table sx={{ borderCollapse: 'separate', borderSpacing: '0 4px', tableLayout: 'fixed' }}>
                  {index === 0 && (
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{
                          fontWeight: '700',
                          fontSize: '0.875rem',
                          py: 1.5,
                          border: 'none',
                          color: '#666',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          width: '40%'
                        }}>
                          Event
                        </TableCell>
                        <TableCell sx={{
                          fontWeight: '700',
                          fontSize: '0.875rem',
                          py: 1.5,
                          border: 'none',
                          color: '#666',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          width: '25%'
                        }}>
                          Location
                        </TableCell>
                        <TableCell align="center" sx={{
                          fontWeight: '700',
                          fontSize: '0.875rem',
                          py: 1.5,
                          border: 'none',
                          color: '#666',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          width: '15%'
                        }}>
                          Drummers
                        </TableCell>
                        <TableCell align="right" sx={{
                          fontWeight: '700',
                          fontSize: '0.875rem',
                          py: 1.5,
                          border: 'none',
                          color: '#666',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          width: '20%'
                        }}>
                          Price
                        </TableCell>
                      </TableRow>
                    </TableHead>
                  )}
                  <TableBody>
                    <TableRow
                      sx={{
                        backgroundColor: 'white',
                        boxShadow: cardShadow,
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                          transform: 'translateY(-2px)'
                        },
                        transition: 'all 0.2s ease',
                        '& td:first-of-type': {
                          borderTopLeftRadius: `${cardRadius * 4}px`,
                          borderBottomLeftRadius: event.hasOptions ? 0 : `${cardRadius * 4}px`,
                        },
                        '& td:last-of-type': {
                          borderTopRightRadius: `${cardRadius * 4}px`,
                          borderBottomRightRadius: event.hasOptions ? 0 : `${cardRadius * 4}px`,
                        }
                      }}
                    >
                      <TableCell sx={{ py: 2, px: 2, border: 'none', width: '40%' }}>
                        <Typography variant="body1" fontWeight="600" fontSize="1rem" noWrap={false} sx={{ wordBreak: 'break-word' }}>
                          {event.name || `Event ${index + 1}`}
                        </Typography>
                        {event.startTime && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {formatStartTime(event.startTime)}
                          </Typography>
                        )}
                        {shouldShowOptionSelector(event) && (
                          <Chip
                            label="Multiple options"
                            size="small"
                            sx={{
                              mt: 0.5,
                              backgroundColor: '#f3e5f5',
                              color: '#380045',
                              fontSize: '0.7rem'
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 2, px: 2, border: 'none', width: '25%' }}>
                        <Typography variant="body1" fontSize="1rem" fontWeight="500" color="text.primary" sx={{ wordBreak: 'break-word' }}>
                          {event.location}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2, px: 2, border: 'none', width: '15%' }}>
                        {shouldShowOptionSelector(event) ? (
                          <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            See options
                          </Typography>
                        ) : (
                          <Typography variant="body1" fontSize="1rem" fontWeight="600">
                            {formatNumber(getEventDisplayData(event).numberOfDrummers)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2, px: 2, border: 'none', width: '20%' }}>
                        {shouldShowOptionSelector(event) ? (
                          <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            Select below
                          </Typography>
                        ) : (
                          <Typography variant="h6" fontWeight="700" color="#380045">
                            £{formatPrice(getEventDisplayData(event).price)}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Options Selector - Only show when not accepted */}
              {shouldShowOptionSelector(event) && (
                <Box sx={{
                  backgroundColor: '#fafafa',
                  p: 3,
                  borderBottomLeftRadius: `${cardRadius * 4}px`,
                  borderBottomRightRadius: `${cardRadius * 4}px`,
                  boxShadow: cardShadow
                }}>
                  <OptionSelector
                    event={event}
                    onOptionSelect={handleOptionSelect}
                    localSelectedId={localSelections[event.id]}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Box>
      ) : (
        /* Mobile View - Modern Card Style */
        <Box sx={{ mb: 4 }}>
          {eventsList.map((event, index) => (
            <Box
              key={event.id || index}
              sx={{
                mb: 2.5,
                backgroundColor: 'white',
                borderRadius: cardRadius,
                overflow: 'hidden',
                boxShadow: cardShadow,
                border: '1px solid rgba(0,0,0,0.05)'
              }}
            >
              {/* Header */}
              <Box sx={{
                p: 2,
                backgroundColor: '#faf9f6',
                borderBottom: '1px solid rgba(0,0,0,0.04)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <EventIcon sx={{ color: '#380045', fontSize: '1.2rem', opacity: 0.8 }} />
                  <Typography variant="subtitle1" fontWeight="700" color="#380045" sx={{ lineHeight: 1.3 }}>
                    {event.name || `Event ${index + 1}`}
                  </Typography>
                </Box>
                {shouldShowOptionSelector(event) && (
                  <Chip
                    label="Options"
                    size="small"
                    sx={{
                      backgroundColor: '#f3e5f5',
                      color: '#380045',
                      fontSize: '0.7rem'
                    }}
                  />
                )}
              </Box>

              {/* Data Rows */}
              <Box sx={{ p: 2 }}>
                <Stack spacing={2.5}>

                  {/* Location & Start Time Row */}
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <LocationOn sx={{ color: '#666', fontSize: '1.2rem', mt: 0.3 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="600" textTransform="uppercase" letterSpacing="0.5px" display="block" gutterBottom>
                        Location
                      </Typography>
                      <Typography variant="body2" fontWeight="600" sx={{ wordBreak: 'break-word', color: '#333' }}>
                        {event.location}
                      </Typography>
                    </Box>
                    {event.startTime && (
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="600" textTransform="uppercase" letterSpacing="0.5px" display="block" gutterBottom>
                          Start Time
                        </Typography>
                        <Typography variant="body2" fontWeight="600" color="#333">
                          {formatStartTime(event.startTime)}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Divider light />

                  {/* Options Selector or Price Display */}
                  {shouldShowOptionSelector(event) ? (
                    <OptionSelector
                      event={event}
                      onOptionSelect={handleOptionSelect}
                      localSelectedId={localSelections[event.id]}
                    />
                  ) : (
                    /* Grid for Drummers & Price - shows for regular events OR accepted events with options */
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>

                      {/* Drummers */}
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <People sx={{ color: '#666', fontSize: '1.2rem' }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight="600" textTransform="uppercase" letterSpacing="0.5px" display="block">
                            Drummers
                          </Typography>
                          <Typography variant="body1" fontWeight="700" color="#333">
                            {formatNumber(getEventDisplayData(event).numberOfDrummers)}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Price */}
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', justifyContent: 'flex-end', textAlign: 'right' }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight="600" textTransform="uppercase" letterSpacing="0.5px" display="block">
                            Price
                          </Typography>
                          <Typography variant="h6" fontWeight="700" color="#380045" sx={{ lineHeight: 1 }}>
                            £{formatPrice(getEventDisplayData(event).price)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                </Stack>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Expenses - additional costs, shown when present. Uses the same card
          language as the event line items but with lighter type and a muted
          price so they read as secondary charges rather than headline events. */}
      {Array.isArray(expenses) && expenses.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '0.875rem',
              px: 2,
              py: 1.5,
              color: '#666',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Additional Costs
          </Typography>
          <Stack spacing={0}>
            {expenses.map((expense, index) => (
              <Box
                key={expense.id || index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                  px: 2,
                  py: 1.5,
                  borderBottom: '1px solid rgba(0,0,0,0.08)',
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body1" fontWeight="500" color="text.primary" sx={{ lineHeight: '24px', wordBreak: 'break-word' }}>
                    {expense.label || '-'}
                  </Typography>
                  {expense.quantity > 1 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
                      £{formatPrice(expense.amount)} × {expense.quantity}
                    </Typography>
                  )}
                </Box>
                <Typography variant="h6" fontWeight="700" color="#380045" sx={{ lineHeight: '32px', whiteSpace: 'nowrap' }}>
                  £{formatPrice(expense.total)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* Total */}
      {showTotal && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />

          <Box
            sx={{
              p: { xs: 1.5, md: 2 },
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Typography variant="body1" fontWeight="500" color="text.secondary">
              Total
            </Typography>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600" color="#380045">
              £{displayTotal.toFixed(2)}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
});

export default QuoteAccordion;
